import {CloudUploadOutlined, ReloadOutlined} from '@ant-design/icons';
import {ValidatorProvider} from '@dofleini/validator';
import {Form, Typography, Upload} from 'antd';
import ImgCrop from 'antd-img-crop';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {Fragment, memo, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {beforeUpload, useUploadAsset} from '@/components/UploadImage/useUploadAsset';
import {getFileNameFromSource, getFromSource} from '@/utils/getImages';

import style from './index.less';

const {Dragger} = Upload;
const {Text} = Typography;

const fileDropFilterFunc = (record, fileDroop, onlyImage) => {
  const imageValue = (onlyImage ? record : (record?.image || record?.thumb));
  const dropValue = (onlyImage ? (fileDroop?.response || fileDroop?._id) : (fileDroop.response?.image || fileDroop.response?.thumb || fileDroop?._id));
  return imageValue !== dropValue;
};

const UploadFileDragger = ({
  onChange,
  value,
  disabled,
  btnText,
  className,
  descriptionText,
  translation,
  multiple,
  cropImage,
  isImage,
  onlyImage,
  icon,
  actionCmp,
  aspect,
  isLoading,
  actionUrl,
  showUploadList,
  ...rest
}) => {
  const {t} = useTranslation(translation || 'common');
  const [files, setFiles] = useState({
    previewVisible: false,
    previewImage: '',
    previewTitle: '',
    fileList: []
  });
  
  const {
    props,
    handleChange,
    loading
  } = useUploadAsset(value, onChange, isImage, onlyImage, multiple, (v) => setFiles(v), actionUrl);
  
  const onRemoveHandler = useCallback((a) => {
    const toSet = multiple ? value?.filter((e) => fileDropFilterFunc(e, a, onlyImage || !isImage)) : null;
    onChange && onChange(toSet);
  }, [isImage, multiple, onChange, onlyImage, value]);
  
  useEffect(() => {
    const defaultText = (isImage ? 'Image' : 'Document');
    if (value) {
      let images;
      if (Array.isArray(value)) {
        images = value.map((data, i) => {
          const dataValue = data?.image || data;
          return {
            _id: data?._id || dataValue,
            uid: i,
            status: 'done',
            name: getFileNameFromSource(dataValue, defaultText),
            url: getFromSource(dataValue)
          };
        });
      } else {
        const dataValue = value?.image || value;
        images = [{
          _id: dataValue,
          uid: dataValue,
          status: 'done',
          name: getFileNameFromSource(dataValue, defaultText),
          url: getFromSource(dataValue)
        }];
      }
      setFiles({fileList: images});
    }
  }, [isImage, value]);
  
  const handleBeforeUpload = useCallback((file) => beforeUpload(file, isImage), [isImage]);
  
  const Component = cropImage ? ImgCrop : Fragment;
  const IconCmp = icon || ((loading || isLoading) ? ReloadOutlined : CloudUploadOutlined);
  const componentProps = cropImage ? {
    rotate: true,
    aspect: aspect,
    crop: {x: 0, y: 0},
    grid: true,
    minZoom: 0,
    zoomSpeed: 1,
    fillColor: '#000',
    cropperProps: {
      restrictPosition: false
    },
    modalTitle: t('editImage')
  } : {};
  
  return (
    <div className={classNames(style.container, 'relative')}>
      <div className={classNames(style.actionKeyContainer, {'hidden': !actionCmp})}>
        {actionCmp}
      </div>
      <Component {...componentProps}>
        <Dragger
          listType="picture-card"
          {...props}
          {...rest}
          beforeUpload={handleBeforeUpload}
          fileList={files?.fileList}
          defaultFileList={[]}
          name="file"
          className={classNames(style.imageUploader, className, {'mb-2': (loading || isLoading)})}
          disabled={disabled}
          onChange={handleChange}
          onRemove={onRemoveHandler}
          showUploadList={showUploadList === undefined ? {showPreviewIcon: false} : showUploadList}
        >
          <div className={classNames('flex flex-row items-center px-4 justify-center', {'opacity-50': disabled})}>
            <IconCmp className="mr-2" spin={(loading || isLoading)}/>
            <div>
              {(loading || isLoading) ? t('loading') :
                <div>
                  {t(descriptionText)} {btnText && <Text className="text-primary">{t(btnText)}</Text>}
                </div>
              }
            </div>
          </div>
        </Dragger>
      </Component>
    </div>
  );
};

export default memo(UploadFileDragger);

UploadFileDragger.defaultProps = {
  multiple: false,
  translation: 'common',
  descriptionText: 'upload',
  aspect: 16 / 9,
  cropImage: false,
  isImage: false,
};

UploadFileDragger.propTypes = {
  multiple: PropTypes.bool,
  innerRef: PropTypes.any,
  maxCount: PropTypes.number,
  actionCmp: PropTypes.any,
  icon: PropTypes.node,
  onlyImage: PropTypes.bool,
  showUploadList: PropTypes.bool,
  isLoading: PropTypes.bool,
  isImage: PropTypes.bool,
  aspect: PropTypes.number,
  translation: PropTypes.string,
  actionUrl: PropTypes.string,
  descriptionText: PropTypes.string,
  button: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  className: PropTypes.string,
  cropImage: PropTypes.bool,
  btnText: PropTypes.string,
};


export const renderUploadFileDragged = ({formItemProps, field, form}) => {
  
  const rules = field?.required && [{required: true, message: ValidatorProvider.t('validateMessages.required')}];
  
  return (
    <Form.Item {...formItemProps} required={field?.required} rules={rules}>
      <UploadFileDragger {...field?.widgetProps} {...field} form={form}/>
    </Form.Item>
  );
};
